import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { DateInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { VocabularyTermPicker } from '@/components/vocabulary/VocabularyTermPicker';

import type { VariantDraft } from '../variantForm';
import type { FieldSpec } from '../variantFields';
import { MultiSelectDropdown } from './MultiSelectDropdown';

interface VariantFieldProps {
  readonly field: FieldSpec;
  readonly draft: VariantDraft;
  readonly onPatch: (patch: VariantDraft) => void;
  readonly disabled?: boolean;
}

/** Renders a single variant field from its spec, wired to the draft record. */
export function VariantField({
  field,
  draft,
  onPatch,
  disabled = false,
}: VariantFieldProps): ReactElement {
  const { t } = useTranslation();
  const label = t(field.labelKey);
  const emptyOption = t('patient.demographics.emptyValue');

  const scalar = (draft[field.key] as string | undefined) ?? '';
  const list = (draft[field.key] as readonly string[] | undefined) ?? [];

  let control: ReactElement;
  switch (field.kind) {
    case 'gene':
      control = (
        <VocabularyTermPicker
          vocabularyId="hgnc"
          value={
            draft.geneSymbol
              ? { id: (draft.geneId as string) || (draft.geneSymbol as string), label: draft.geneSymbol as string }
              : null
          }
          onChange={(next) => {
            onPatch({ geneId: next?.id ?? '', geneSymbol: next?.label ?? '' });
          }}
          ariaLabel={label}
          placeholder={t('patient.geneticInfo.fields.genePlaceholder')}
          disabled={disabled}
          // PTV2-1298: per-result ⓘ detail, matching every other vocabulary picker.
          showInfo
        />
      );
      break;
    case 'select':
      control = (
        <Select
          value={scalar}
          onChange={(e) => {
            onPatch({ [field.key]: e.target.value });
          }}
          disabled={disabled}
          aria-label={label}
          className="h-10 text-sm"
        >
          <option value="">{emptyOption}</option>
          {field.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.labelKey ? t(option.labelKey) : (option.label ?? option.value)}
            </option>
          ))}
        </Select>
      );
      break;
    case 'multiselect':
      control = (
        <MultiSelectDropdown
          options={field.options ?? []}
          value={list}
          onChange={(next) => {
            onPatch({ [field.key]: next });
          }}
          disabled={disabled}
          ariaLabel={label}
        />
      );
      break;
    case 'taglist':
      control = (
        <Input
          value={list.join(', ')}
          onChange={(e) => {
            onPatch({
              [field.key]: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s.length > 0),
            });
          }}
          disabled={disabled}
          aria-label={label}
          placeholder={t('patient.geneticInfo.fields.commaSeparated')}
          className="text-sm"
        />
      );
      break;
    case 'textarea':
      control = (
        <Textarea
          value={scalar}
          rows={2}
          onChange={(e) => {
            onPatch({ [field.key]: e.target.value });
          }}
          disabled={disabled}
          aria-label={label}
          className="text-sm"
        />
      );
      break;
    case 'date':
      control = (
        <DateInput
          value={scalar}
          onChange={(value) => {
            onPatch({ [field.key]: value });
          }}
          disabled={disabled}
        />
      );
      break;
    case 'number':
      control = (
        <Input
          value={scalar}
          inputMode="decimal"
          onChange={(e) => {
            onPatch({ [field.key]: e.target.value });
          }}
          disabled={disabled}
          aria-label={label}
          className="text-sm"
        />
      );
      break;
    default:
      control = (
        <Input
          value={scalar}
          onChange={(e) => {
            onPatch({ [field.key]: e.target.value });
          }}
          disabled={disabled}
          aria-label={label}
          className="text-sm"
        />
      );
  }

  return (
    <div className="space-y-1">
      <span className="block text-xs font-medium text-foreground">
        {label}
        {field.required === true && (
          <span className="ml-0.5 text-destructive" aria-hidden="true">
            *
          </span>
        )}
      </span>
      {control}
    </div>
  );
}
